import { faColumns, faFileChartLine } from '@fortawesome/pro-regular-svg-icons';
import { faCode, faCog, faHeartRate } from '@fortawesome/pro-solid-svg-icons';
import { faGrid2 } from '@fortawesome/sharp-solid-svg-icons';
import { useNavigationDrawerValues } from 'components/navigation_drawer/navigation_drawer_store';
import {
    getDashboardUrl,
    getDeskUrl,
    getHardwareUrl,
    getLandingUrl,
    getMapsUrl,
    getReportsUrl,
    getSettingsUrl,
    getWorkbenchUrl,
} from 'components/navigation_drawer/navigation_routes';
import { NavigationDrawerItemProps } from 'components/navigation_drawer/NavigationDrawerItem';
import { HardwareIcon } from 'components/svg_icon/HardwareIcon';
import { useAccess } from 'contexts/AccessContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { useUser } from 'contexts/UserContext';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { excludeNullAndUndefined } from 'utils/array';

export enum NavigationModule {
    Overview = 'overview',
    Workbench = 'workbench',
    Desk = 'desk',
    Hardware = 'hardware',
    Settings = 'settings',
    Report = 'report',
    Demo = 'demo',
}

const getActiveNavigationModule = (pathname: string): NavigationModule | undefined => {
    if (
        /\/(corporation|location)\/[a-z0-9-]+\/?$/.test(pathname) ||
        pathname.includes('/dashboards') ||
        pathname.includes('/maps')
    ) {
        return NavigationModule.Overview;
    }
    if (
        pathname === '/' ||
        // Check for `/login` to avoid error thrown: because this method is invoked
        // after logging in but before other URL changes (e.g. /login -> /internal)
        pathname.startsWith('/login') ||
        pathname.startsWith('/internal') ||
        pathname.startsWith('/sales') ||
        pathname.startsWith('/sentry')
    ) {
        return NavigationModule.Overview;
    }
    if (pathname.includes('/workbench')) {
        return NavigationModule.Workbench;
    }
    if (pathname.includes('/desk')) {
        return NavigationModule.Desk;
    }
    if (pathname.includes('/hardware')) {
        return NavigationModule.Hardware;
    }
    if (pathname.includes('/settings')) {
        return NavigationModule.Settings;
    }
    if (pathname.includes('/report')) {
        return NavigationModule.Report;
    }
    if (pathname.includes('/demo')) {
        return NavigationModule.Demo;
    }
    return undefined;
};

export function useNavigationConfig() {
    const user = useUser();
    const access = useAccess();
    const permissions = usePermissions();
    const routeEntities = useRouteEntities();
    const { pathname } = useLocation();

    const { setItems, setActiveModule } = useNavigationDrawerValues((state) => ({
        setItems: state.setItems,
        setActiveModule: state.setActiveModule,
    }));

    const {
        dashboardUrl,
        deskUrl,
        hardwareUrl,
        mapUrl,
        overviewUrl,
        reportsUrl,
        settingsUrl,
        workbenchUrl,
    } = useMemo(
        () => ({
            dashboardUrl: getDashboardUrl(access, permissions, routeEntities),
            deskUrl: getDeskUrl(access, routeEntities),
            hardwareUrl: getHardwareUrl(permissions, routeEntities),
            mapUrl: getMapsUrl(access, permissions, routeEntities),
            overviewUrl: getLandingUrl(access, routeEntities),
            reportsUrl: getReportsUrl(permissions, routeEntities),
            settingsUrl: getSettingsUrl(access, user, permissions, routeEntities),
            workbenchUrl: getWorkbenchUrl(access, permissions, routeEntities),
        }),
        [access, permissions, routeEntities, user]
    );

    const activeNavigationModule = useMemo(() => getActiveNavigationModule(pathname), [
        pathname,
    ]);

    const isGlobalOverview =
        routeEntities.corporation === undefined && routeEntities.location === undefined;

    const navigationConfig: NavigationDrawerItemProps[] = useMemo(
        () =>
            excludeNullAndUndefined<NavigationDrawerItemProps>([
                overviewUrl
                    ? {
                          id: NavigationModule.Overview,
                          title: 'Overview',
                          route: overviewUrl,
                          icon: faGrid2,
                          enabled: !isGlobalOverview,
                          isActive: () =>
                              activeNavigationModule === NavigationModule.Overview,
                          secondaryNavigationItems: excludeNullAndUndefined([
                              {
                                  id: 'summary',
                                  title: 'Summary',
                                  route: overviewUrl,
                                  enabled: true,
                                  exact: true,
                              },
                              dashboardUrl
                                  ? {
                                        id: 'dashboards',
                                        title: 'Dashboards',
                                        route: dashboardUrl,
                                        enabled: true,
                                        exact: true,
                                    }
                                  : undefined,
                              mapUrl
                                  ? {
                                        id: 'map',
                                        title: 'Maps',
                                        route: mapUrl,
                                        enabled: true,
                                        exact: true,
                                    }
                                  : undefined,
                          ]),
                      }
                    : undefined,
                overviewUrl
                    ? {
                          id: NavigationModule.Overview,
                          title: 'Overview',
                          route: overviewUrl,
                          icon: faGrid2,
                          enabled: isGlobalOverview,
                          isActive: () =>
                              activeNavigationModule === NavigationModule.Overview,
                      }
                    : undefined,
                workbenchUrl
                    ? {
                          id: NavigationModule.Workbench,
                          title: 'Workbench',
                          route: workbenchUrl,
                          icon: faHeartRate,
                          enabled: true,
                          exact: false,
                          isActive: () =>
                              activeNavigationModule === NavigationModule.Workbench,
                      }
                    : undefined,
                deskUrl
                    ? {
                          id: NavigationModule.Desk,
                          title: 'Desk',
                          route: deskUrl,
                          icon: faColumns,
                          enabled: true,
                          exact: false,
                          isActive: () =>
                              activeNavigationModule === NavigationModule.Desk,
                          secondaryNavigationItems: [
                              {
                                  id: 'machine-health-issues',
                                  title: 'Machine Health',
                                  route: `${deskUrl}machineHealth`,
                                  enabled: routeEntities.location !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'hardware-health-issues',
                                  title: 'Hardware Health',
                                  route: `${deskUrl}hardwareHealth`,
                                  enabled:
                                      routeEntities.location !== undefined &&
                                      permissions.issue.canViewHardwareIssues,
                                  exact: false,
                              },
                          ],
                      }
                    : undefined,
                hardwareUrl
                    ? {
                          id: NavigationModule.Hardware,
                          title: 'Hardware',
                          route: hardwareUrl,
                          customIcon: <HardwareIcon type="baseStation" size={20} />,
                          enabled:
                              routeEntities.location !== undefined ||
                              permissions.canViewHardwareAdmin ||
                              (permissions.canViewContainerManagement &&
                                  routeEntities.corporation !== undefined),
                          exact: false,
                          isActive: () =>
                              activeNavigationModule === NavigationModule.Hardware,
                          secondaryNavigationItems: [
                              {
                                  id: 'hardware-overview',
                                  title: 'Hardware Overview',
                                  route: `${hardwareUrl}overview`,
                                  enabled: routeEntities.location !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'admin-setup',
                                  title: 'Admin Setup',
                                  route: `${hardwareUrl}adminSetup`,
                                  enabled: permissions.canViewHardwareAdmin,
                                  exact: false,
                              },
                              {
                                  id: 'container-management',
                                  title: 'Container Management',
                                  route: `${hardwareUrl}containerManagement`,
                                  enabled:
                                      permissions.canViewContainerManagement &&
                                      routeEntities.corporation !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'alert-log',
                                  title: 'Alert Log',
                                  route: `${hardwareUrl}alertLog`,
                                  enabled: routeEntities.location !== undefined,
                                  exact: false,
                              },
                          ],
                      }
                    : undefined,
                settingsUrl
                    ? {
                          id: NavigationModule.Settings,
                          title: 'Settings',
                          route: settingsUrl,
                          icon: faCog,
                          enabled: true,
                          exact: false,
                          isActive: () =>
                              activeNavigationModule === NavigationModule.Settings,
                          secondaryNavigationItems: [
                              {
                                  id: 'models',
                                  title: 'Analytic Models',
                                  route: `${settingsUrl}/models`,
                                  enabled: permissions.canViewModel,
                                  exact: false,
                              },
                              {
                                  id: 'application-types',
                                  title: 'Application Types',
                                  route: `${settingsUrl}/applicationTypes`,
                                  enabled:
                                      permissions.canViewAssemblyTypeSettings &&
                                      !routeEntities.corporation &&
                                      !routeEntities.location,
                                  exact: false,
                              },
                              {
                                  id: 'asset-types',
                                  title: 'Asset Types',
                                  route: `${settingsUrl}/assetTypes`,
                                  enabled:
                                      permissions.canViewAssetTypeSettings &&
                                      !routeEntities.corporation &&
                                      !routeEntities.location,
                                  exact: false,
                              },
                              {
                                  id: 'bearings',
                                  title: 'Bearings',
                                  route: `${settingsUrl}/bearings`,
                                  enabled: permissions.canViewBearingsSettings,
                                  exact: false,
                              },
                              {
                                  id: 'corporation',
                                  title: 'Corporation',
                                  route: `${settingsUrl}/corporation`,
                                  enabled:
                                      permissions.canViewCorporationsSettings &&
                                      routeEntities.corporation !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'corporations',
                                  title: 'Corporations',
                                  route: `${settingsUrl}/corporations`,
                                  enabled:
                                      permissions.canViewCorporationsSettings &&
                                      !routeEntities.corporation,
                                  exact: false,
                              },
                              {
                                  id: 'file-library',
                                  title: 'File Library',
                                  route: `${settingsUrl}/fileLibrary`,
                                  enabled:
                                      permissions.canViewFileLibrary &&
                                      routeEntities.corporation !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'identity-providers',
                                  title: 'Identity Providers',
                                  route: `${settingsUrl}/identityProviders`,
                                  enabled:
                                      permissions.canViewIdentityProviders &&
                                      !routeEntities.corporation &&
                                      !routeEntities.location,
                                  exact: false,
                              },
                              {
                                  id: 'industries',
                                  title: 'Industries',
                                  route: `${settingsUrl}/industries`,
                                  enabled:
                                      permissions.canViewIndustries &&
                                      !routeEntities.corporation &&
                                      !routeEntities.location,
                                  exact: false,
                              },
                              {
                                  id: 'Integrations',
                                  title: 'Integrations',
                                  route: `${settingsUrl}/integrations`,
                                  enabled:
                                      permissions.canViewIntegrations &&
                                      routeEntities.location !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'location',
                                  title: 'Location',
                                  route: `${settingsUrl}/location`,
                                  enabled:
                                      permissions.canViewLocationsSettings &&
                                      routeEntities.location !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'locations',
                                  title: 'Locations',
                                  route: `${settingsUrl}/locations`,
                                  enabled:
                                      permissions.canViewLocationsSettings &&
                                      !routeEntities.location,
                                  exact: false,
                              },
                              {
                                  id: 'notification-subscriptions',
                                  title: 'Notification Subscriptions',
                                  route: `${settingsUrl}/notificationSubscriptions`,
                                  enabled:
                                      permissions.canViewNotificationSubscriptionsSettings,
                                  exact: false,
                              },
                              {
                                  id: 'setup-tools',
                                  title: 'Setup Tools',
                                  route: `${settingsUrl}/setupTools`,
                                  enabled: permissions.canViewSetupTools,
                                  exact: false,
                              },
                              {
                                  id: 'templates',
                                  title: 'Templates',
                                  route: `${settingsUrl}/templates`,
                                  enabled:
                                      permissions.canViewTemplates &&
                                      routeEntities.location !== undefined,
                                  exact: false,
                              },
                              {
                                  id: 'third-party-sensors',
                                  title: 'Third Party Sensor Models',
                                  route: `${settingsUrl}/thirdPartySensors`,
                                  enabled:
                                      permissions.canViewThirdPartySensors &&
                                      isGlobalOverview,
                                  exact: false,
                              },
                              {
                                  id: 'time-ranges',
                                  title: 'Time Ranges',
                                  route: `${settingsUrl}/timeRanges`,
                                  enabled:
                                      permissions.canViewTimeRangesSettings &&
                                      isGlobalOverview,
                                  exact: false,
                              },
                              {
                                  id: 'users',
                                  title: 'Users',
                                  route: `${settingsUrl}/users`,
                                  enabled: permissions.canViewUsers,
                                  exact: false,
                              },
                          ],
                      }
                    : undefined,
                reportsUrl
                    ? {
                          id: NavigationModule.Report,
                          title: 'Reports',
                          route: reportsUrl,
                          icon: faFileChartLine,
                          enabled: true,
                          exact: false,
                          isActive: () =>
                              activeNavigationModule === NavigationModule.Report,
                      }
                    : undefined,
                {
                    id: NavigationModule.Demo,
                    title: 'Demo',
                    route: '/demo',
                    icon: faCode,
                    enabled: process.env.NODE_ENV === 'development',
                },
            ]),
        [
            activeNavigationModule,
            permissions,
            routeEntities,
            isGlobalOverview,
            dashboardUrl,
            deskUrl,
            hardwareUrl,
            mapUrl,
            overviewUrl,
            reportsUrl,
            settingsUrl,
            workbenchUrl,
        ]
    );

    useEffect(() => {
        setItems(navigationConfig);
        setActiveModule(navigationConfig.find(({ id }) => id === activeNavigationModule));
    }, [activeNavigationModule, navigationConfig, setActiveModule, setItems]);
}
